import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Card,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  getEligibleManagers,
  getDirectReports,
  getEligibleNewManagersForRole,
  getTeamPendingApprovals,
  executeRearrangeTeamFlow,
} from "../../Services/WorkTransferApis";
import WorkTransferNotice from "../../Components/WorkTransfer/WorkTransferNotice";
import { COLORS, PageHeader, Field, FieldLabel, PrimaryButton, SecondaryButton, fieldSx, cardSx, CircleCheckbox } from "../../Components/WorkTransfer/wtStyle";

// "Rearrange a team" (rebuilt 2026-08-27 to match
// work-transfer-module/work-transfer-demo.html's actual interaction: one
// working screen, tick a subset of a manager's direct reports, assign them to
// a recipient, repeat with a different subset/recipient — so one team can
// split across several new managers in a single Review & Confirm (demo
// scenario C: "8 → Rohan, 7 → Priya, 5 unchanged"). No Stepper wizard, unlike
// the previous build — that only supported one new manager per run.
//
// Approval carry-forward (Cancellation/Unit Shift/Site Visit) is kept even
// though the demo doesn't have it at all — without it, a pending approval for
// a moved report's lead is stuck with the old manager with no way to fix it
// from this screen.
//
// BRIDGE NOTE: the backend (executeRearrangeTeamFlow) still only accepts one
// {reportUserIds, newManagerId} group per call. Until it's rebuilt to accept
// several groups atomically, Confirm below submits one sequential call per
// destination group — each becomes its own Transfer History batch, and a
// failure partway through does NOT roll back groups that already succeeded.
// Flagged in the UI copy below; replace with one atomic call once the
// backend is rebuilt to match.

const APPROVER_LABEL = "the old manager";

// Shared by every user-picker Autocomplete on this page — sandbox data has
// duplicate names (e.g. two "Suraj Karkanti" under the same manager, distinct
// only by email/userId), so name alone is not enough to tell options apart.
// Two different shapes come through here: getEligibleManagers's `roleId(title)`
// join (u.roleId.title) vs getDirectReportUsers/getEligibleNewManagersForRole's
// bare-scalar-plus-alias shape (u.role.title) — checks both.
function renderUserOption(props, u) {
  const roleTitle = u.role?.title || u.roleId?.title;
  return (
    <Box component="li" {...props} key={u.userId}>
      <Box>
        <Typography fontSize={13.5} fontWeight={600}>
          {u.name}
          {roleTitle && (
            <Typography component="span" fontSize={11} color={COLORS.blue} sx={{ ml: 0.75, fontWeight: 600 }}>
              {roleTitle}
            </Typography>
          )}
          {typeof u.directReportsCount === "number" && (
            <Typography component="span" fontSize={11} color={COLORS.textSecondary} sx={{ ml: 0.75 }}>
              · {u.directReportsCount} report{u.directReportsCount === 1 ? "" : "s"}
            </Typography>
          )}
        </Typography>
        <Typography fontSize={12} color={COLORS.textSecondary}>
          {u.email}
        </Typography>
      </Box>
    </Box>
  );
}

// Toggle-style filter chip, reused for the report/manager role filters below.
function FilterChip({ label, active, onClick }) {
  return (
    <Chip
      label={label}
      size="small"
      onClick={onClick}
      sx={{
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        bgcolor: active ? COLORS.blue : COLORS.sunken,
        color: active ? "#fff" : COLORS.textSecondary,
        border: `1px solid ${active ? COLORS.blue : COLORS.border}`,
      }}
    />
  );
}

export default function RearrangeTeamPage({ canExecute = false }) {
  const [managers, setManagers] = useState([]);
  const [manager, setManager] = useState(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reports, setReports] = useState([]);

  const [checkedReportIds, setCheckedReportIds] = useState([]);
  const [bulkRecipient, setBulkRecipient] = useState(null);
  const [assignments, setAssignments] = useState({}); // { [reportUserId]: recipientUserObj }

  // Role-hierarchy-restricted "new manager" options (Roles.parentRoleId) —
  // e.g. ticking a Sales Manager only offers Project Heads. Loaded fresh
  // whenever the first report of a new batch is ticked; requiredRoleId stays
  // null until then, and also for a role with no parentRoleId (top of chain).
  const [roleScopedRecipients, setRoleScopedRecipients] = useState({ requiredRoleId: null, requiredRoleTitle: null, users: [] });
  const [loadingRoleScopedRecipients, setLoadingRoleScopedRecipients] = useState(false);

  const [reviewReady, setReviewReady] = useState(false);
  // Fetched once when the team loads (scoped to EVERY report, not just
  // ticked ones) so a per-row "pending approvals" badge can show up front —
  // the Review step below filters this down to just the assigned reports.
  const [pendingApprovals, setPendingApprovals] = useState({ cancelRequests: [], changeUnitRequests: [], siteVisitApprovals: [] });
  const [carryForwardCancelIds, setCarryForwardCancelIds] = useState([]);
  const [carryForwardChangeUnitIds, setCarryForwardChangeUnitIds] = useState([]);
  const [carryForwardSiteVisitApprovalIds, setCarryForwardSiteVisitApprovalIds] = useState([]);

  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [notice, setNotice] = useState(null);

  // Report-checklist filters — pure client-side, the team's already loaded.
  const [reportSearch, setReportSearch] = useState("");
  const [reportHasTeamOnly, setReportHasTeamOnly] = useState(false);
  const [reportRoleFilter, setReportRoleFilter] = useState(null);
  const [managerRoleFilter, setManagerRoleFilter] = useState(null);

  useEffect(() => {
    getEligibleManagers()
      .then((res) => {
        if (res.success) setManagers(res.data.users);
      })
      .catch(() => setNotice({ type: "error", title: "Couldn't load managers", message: "Try reloading the page." }));
  }, []);

  const resetReview = () => {
    setReviewReady(false);
    setCarryForwardCancelIds([]);
    setCarryForwardChangeUnitIds([]);
    setCarryForwardSiteVisitApprovalIds([]);
  };

  const resetLoadedWork = () => {
    setReports([]);
    setCheckedReportIds([]);
    setBulkRecipient(null);
    setRoleScopedRecipients({ requiredRoleId: null, requiredRoleTitle: null, users: [] });
    setAssignments({});
    setPendingApprovals({ cancelRequests: [], changeUnitRequests: [], siteVisitApprovals: [] });
    setReportSearch("");
    setReportHasTeamOnly(false);
    setReportRoleFilter(null);
    resetReview();
    setReason("");
  };

  const loadReports = async () => {
    if (!manager) {
      setNotice({ type: "warning", title: "Pick a manager first", message: "Select the manager whose team you're rearranging." });
      return;
    }
    setLoadingReports(true);
    setReports([]);
    setCheckedReportIds([]);
    setBulkRecipient(null);
    setRoleScopedRecipients({ requiredRoleId: null, requiredRoleTitle: null, users: [] });
    setAssignments({});
    setPendingApprovals({ cancelRequests: [], changeUnitRequests: [], siteVisitApprovals: [] });
    setReportSearch("");
    setReportHasTeamOnly(false);
    setReportRoleFilter(null);
    resetReview();
    try {
      const res = await getDirectReports(manager.userId);
      if (!res.success) {
        setNotice({ type: "error", title: "Couldn't load team", message: res.message });
        return;
      }
      setReports(res.data.reports);
      if (res.data.reports.length === 0) {
        setNotice({ type: "warning", title: "No direct reports", message: `${manager.name} has no one reporting to them.` });
      } else {
        // Eager, whole-team fetch — powers the per-row "pending approvals"
        // badge below even for reports not ticked yet. Best-effort: a
        // failure here shouldn't block loading the team itself.
        const allReportIds = res.data.reports.map((r) => r.userId);
        getTeamPendingApprovals({ managerId: manager.userId, reportUserIds: allReportIds })
          .then((appRes) => {
            if (appRes.success) setPendingApprovals(appRes.data);
          })
          .catch(() => {});
      }
    } catch (err) {
      setNotice({ type: "error", title: "Couldn't load team", message: err.message });
    } finally {
      setLoadingReports(false);
    }
  };

  const loadRoleScopedRecipients = async (roleId) => {
    setLoadingRoleScopedRecipients(true);
    setBulkRecipient(null);
    try {
      const res = await getEligibleNewManagersForRole(roleId);
      if (res.success) setRoleScopedRecipients(res.data);
    } catch (err) {
      setNotice({ type: "error", title: "Couldn't load eligible new managers", message: err.message });
    } finally {
      setLoadingRoleScopedRecipients(false);
    }
  };

  // Only reports sharing the same role can be ticked together in one batch —
  // their required new-manager role (Roles.parentRoleId) could otherwise
  // differ, and "Assign selected to…" only ever shows one role's options at
  // a time. Ticking the FIRST report of a fresh batch loads that role's
  // options; unticking back to zero clears them.
  const toggleChecked = (userId) => {
    if (checkedReportIds.includes(userId)) {
      const next = checkedReportIds.filter((id) => id !== userId);
      setCheckedReportIds(next);
      if (next.length === 0) {
        setRoleScopedRecipients({ requiredRoleId: null, requiredRoleTitle: null, users: [] });
        setBulkRecipient(null);
      }
      return;
    }
    const report = reports.find((r) => r.userId === userId);
    if (checkedReportIds.length > 0) {
      const firstReport = reports.find((r) => r.userId === checkedReportIds[0]);
      if (firstReport?.roleId !== report?.roleId) {
        setNotice({
          type: "warning",
          title: "One role at a time",
          message: `You've already ticked ${firstReport?.role?.title || "a"} report(s) — untick them first to switch to ${report?.role?.title || "a different role"}.`,
        });
        return;
      }
    }
    setCheckedReportIds((prev) => [...prev, userId]);
    if (checkedReportIds.length === 0 && report) {
      loadRoleScopedRecipients(report.roleId);
    }
  };

  // Excludes the current manager and whoever's ticked right now — a report
  // can't become its own new manager in the same assign action.
  const eligibleBulkRecipients = roleScopedRecipients.users.filter((u) => u.userId !== manager?.userId && !checkedReportIds.includes(u.userId));

  const assignSelected = () => {
    if (checkedReportIds.length === 0) {
      setNotice({ type: "warning", title: "Tick who's moving", message: "Select at least one direct report before assigning." });
      return;
    }
    if (!bulkRecipient) {
      setNotice({ type: "warning", title: "Choose a recipient", message: "Pick a new manager from the dropdown before assigning." });
      return;
    }
    setAssignments((prev) => {
      const next = { ...prev };
      checkedReportIds.forEach((id) => (next[id] = bulkRecipient));
      return next;
    });
    setCheckedReportIds([]);
    setBulkRecipient(null);
    setRoleScopedRecipients({ requiredRoleId: null, requiredRoleTitle: null, users: [] });
    resetReview();
  };

  const removeAssignment = (userId) => {
    setAssignments((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
    resetReview();
  };

  const clearAllAssignments = () => {
    setAssignments({});
    resetReview();
  };

  const toggleCarryForwardCancel = (id) =>
    setCarryForwardCancelIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleCarryForwardChangeUnit = (id) =>
    setCarryForwardChangeUnitIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleCarryForwardSiteVisit = (id) =>
    setCarryForwardSiteVisitApprovalIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const assignedReportIds = Object.keys(assignments);
  const unassignedReports = reports.filter((r) => !assignments[r.userId]);

  // Group assignments by recipient — this is the batch-of-groups the demo's
  // "8 → Rohan, 7 → Priya, 5 unchanged" scenario needs.
  const groups = {};
  assignedReportIds.forEach((reportId) => {
    const recipient = assignments[reportId];
    if (!groups[recipient.userId]) groups[recipient.userId] = { recipient, reportIds: [] };
    groups[recipient.userId].reportIds.push(reportId);
  });
  const groupList = Object.values(groups);

  // pendingApprovals is already loaded whole-team (see loadReports) — Review
  // just needs to filter it down to the reports actually assigned in this
  // batch, no second fetch.
  const goToReview = () => {
    if (assignedReportIds.length === 0) {
      setNotice({ type: "warning", title: "Assign at least one report", message: 'Tick reports, pick a recipient, and click "Assign selected" first.' });
      return;
    }
    setReviewReady(true);
  };

  const assignedReportIdSet = new Set(assignedReportIds);
  const APPROVAL_KINDS = [
    {
      key: "CancellationApproval",
      label: "Cancellation approval",
      items: pendingApprovals.cancelRequests.filter((r) => assignedReportIdSet.has(r.salesid)),
      idField: "cancelUnitRequestId",
      selectedIds: carryForwardCancelIds,
      onToggle: toggleCarryForwardCancel,
    },
    {
      key: "UnitShiftApproval",
      label: "Unit shift approval",
      items: pendingApprovals.changeUnitRequests.filter((r) => assignedReportIdSet.has(r.salesid)),
      idField: "changeUnitRequestId",
      selectedIds: carryForwardChangeUnitIds,
      onToggle: toggleCarryForwardChangeUnit,
    },
    {
      key: "SiteVisitApproval",
      label: `Site visit approval pending with ${APPROVER_LABEL}`,
      items: pendingApprovals.siteVisitApprovals.filter((r) => assignedReportIdSet.has(r.salesid)),
      idField: "leadId",
      selectedIds: carryForwardSiteVisitApprovalIds,
      onToggle: toggleCarryForwardSiteVisit,
    },
  ];
  const totalApprovalsCarriedForward =
    carryForwardCancelIds.length + carryForwardChangeUnitIds.length + carryForwardSiteVisitApprovalIds.length;
  const totalPendingApprovals = APPROVAL_KINDS.reduce((sum, k) => sum + k.items.length, 0);

  // Per-report pending-approval count, from the SAME whole-team fetch — shown
  // as a badge on the checklist so it's visible before a report is even
  // ticked, not just after reaching Review.
  const pendingApprovalCountByReportId = {};
  [...pendingApprovals.cancelRequests, ...pendingApprovals.changeUnitRequests, ...pendingApprovals.siteVisitApprovals].forEach((r) => {
    if (r.salesid) pendingApprovalCountByReportId[r.salesid] = (pendingApprovalCountByReportId[r.salesid] || 0) + 1;
  });

  const summaryLines = [
    ...groupList.map((g) => {
      const before = g.recipient.directReportsCount ?? 0;
      return `${g.reportIds.length} report${g.reportIds.length === 1 ? "" : "s"} → ${g.recipient.name} (${before} → ${before + g.reportIds.length} report${before + g.reportIds.length === 1 ? "" : "s"}).`;
    }),
    ...(unassignedReports.length > 0 ? [`${unassignedReports.length} unchanged (still reports to ${manager?.name}).`] : []),
    "Any moved report's leads still routed to the old manager for Site Visit approval will move with them.",
    ...(totalApprovalsCarriedForward > 0
      ? [`Approvals carried forward: ${carryForwardCancelIds.length} cancellation, ${carryForwardChangeUnitIds.length} unit shift, ${carryForwardSiteVisitApprovalIds.length} site visit.`]
      : []),
  ];

  const confirmDisabled = confirming || !canExecute || reason.trim().length === 0;

  // Checklist filters — pure client-side, the whole team's already in memory.
  const distinctReportRoles = [...new Set(reports.map((r) => r.role?.title).filter(Boolean))];
  const filteredReports = reports.filter((r) => {
    if (reportHasTeamOnly && !(r.directReportsCount > 0)) return false;
    if (reportRoleFilter && r.role?.title !== reportRoleFilter) return false;
    if (reportSearch.trim() && !r.name.toLowerCase().includes(reportSearch.trim().toLowerCase())) return false;
    return true;
  });

  const distinctManagerRoles = [...new Set(managers.map((m) => m.role?.title || m.roleId?.title).filter(Boolean))];
  const filteredManagers = managerRoleFilter
    ? managers.filter((m) => (m.role?.title || m.roleId?.title) === managerRoleFilter)
    : managers;

  const confirmTransfer = async () => {
    setConfirming(true);
    try {
      const performedBy = JSON.parse(localStorage.getItem("user"))?.userId;
      let totalApplied = 0;
      const failedGroups = [];
      for (const g of groupList) {
        const groupIdSet = new Set(g.reportIds);
        const cancelIds = carryForwardCancelIds.filter((id) => {
          const rec = pendingApprovals.cancelRequests.find((r) => r.cancelUnitRequestId === id);
          return rec && groupIdSet.has(rec.salesid);
        });
        const changeIds = carryForwardChangeUnitIds.filter((id) => {
          const rec = pendingApprovals.changeUnitRequests.find((r) => r.changeUnitRequestId === id);
          return rec && groupIdSet.has(rec.salesid);
        });
        const siteVisitIds = carryForwardSiteVisitApprovalIds.filter((id) => {
          const rec = pendingApprovals.siteVisitApprovals.find((r) => r.leadId === id);
          return rec && groupIdSet.has(rec.salesid);
        });
        // eslint-disable-next-line no-await-in-loop
        const res = await executeRearrangeTeamFlow({
          managerId: manager.userId,
          reportUserIds: g.reportIds,
          newManagerId: g.recipient.userId,
          cancelRequestIds: cancelIds,
          changeUnitRequestIds: changeIds,
          siteVisitApprovalIds: siteVisitIds,
          reason,
          performedBy,
        });
        if (!res.success) {
          failedGroups.push(`${g.recipient.name}: ${res.message}`);
        } else {
          totalApplied += res.data.operationsApplied || 0;
        }
      }
      if (failedGroups.length > 0) {
        setNotice({
          type: "error",
          title: totalApplied > 0 ? "Partially completed" : "Transfer failed",
          message: (totalApplied > 0 ? `${totalApplied} record(s) updated before this failed. ` : "") + `Failed: ${failedGroups.join("; ")}`,
        });
        return;
      }
      setNotice({
        type: "success",
        title: "Team rearranged",
        message: `${totalApplied} record(s) updated across ${groupList.length} recipient${groupList.length === 1 ? "" : "s"}.`,
      });
      setManager(null);
      resetLoadedWork();
    } catch (err) {
      setNotice({ type: "error", title: "Transfer failed", message: err.message });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Box sx={{ p: 3, display: "flex", gap: 3, alignItems: "flex-start" }}>
      {/* maxWidth: "none" (not a fixed cap) — matches Move Someone's Work's
          2026-08-24 full-Chrome-width fix: cards/lists fill the available
          window width instead of being squeezed into a narrow column with a
          large empty gutter on a normal desktop browser. */}
      <Box sx={{ maxWidth: "none", width: "100%", flexShrink: 0 }}>
        <Box sx={{ position: "sticky", top: 0, zIndex: 5, bgcolor: "#fff", pb: 1 }}>
          <PageHeader
            title="Rearrange a team"
            subtitle="Tick a subset of a manager's direct reports, assign them to a new manager, and repeat with different subsets — one team can split across several new managers in a single confirm."
          />
        </Box>

        <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 2, mb: distinctManagerRoles.length > 1 ? 1 : 2, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Field label="Current manager" sx={{ width: 300 }}>
              <Autocomplete
                options={filteredManagers}
                getOptionLabel={(u) => u.name || ""}
                isOptionEqualToValue={(o, v) => o.userId === v.userId}
                renderOption={renderUserOption}
                value={manager}
                onChange={(e, val) => {
                  setManager(val);
                  resetLoadedWork();
                }}
                renderInput={(params) => <TextField {...params} placeholder="Choose a manager" />}
                sx={fieldSx}
              />
            </Field>
            <PrimaryButton onClick={loadReports} disabled={loadingReports}>
              {loadingReports ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Load team"}
            </PrimaryButton>
          </Box>
          {distinctManagerRoles.length > 1 && (
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2 }}>
              <FilterChip label="All roles" active={!managerRoleFilter} onClick={() => setManagerRoleFilter(null)} />
              {distinctManagerRoles.map((title) => (
                <FilterChip key={title} label={title} active={managerRoleFilter === title} onClick={() => setManagerRoleFilter(title)} />
              ))}
            </Box>
          )}

          {reports.length > 0 && (
            <>
              <Box sx={{ display: "flex", gap: 1.5, mb: 0.5, flexWrap: "wrap", alignItems: "flex-end" }}>
                <Field label="Assign selected to…" sx={{ width: 260 }}>
                  <Autocomplete
                    options={eligibleBulkRecipients}
                    getOptionLabel={(u) => u.name || ""}
                    isOptionEqualToValue={(o, v) => o.userId === v.userId}
                    renderOption={renderUserOption}
                    value={bulkRecipient}
                    onChange={(e, val) => setBulkRecipient(val)}
                    disabled={checkedReportIds.length === 0 || loadingRoleScopedRecipients}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={loadingRoleScopedRecipients ? "Loading…" : "Choose new manager"}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: loadingRoleScopedRecipients ? <CircularProgress size={14} /> : params.InputProps.endAdornment,
                        }}
                      />
                    )}
                    sx={fieldSx}
                  />
                </Field>
                <SecondaryButton onClick={assignSelected}>Assign selected</SecondaryButton>
              </Box>
              {checkedReportIds.length > 0 && (
                <Typography fontSize={12} sx={{ mb: 1.5 }} color={roleScopedRecipients.requiredRoleId ? COLORS.textSecondary : COLORS.red}>
                  {roleScopedRecipients.requiredRoleId
                    ? `New manager must hold the ${roleScopedRecipients.requiredRoleTitle} role, per the reporting hierarchy.`
                    : loadingRoleScopedRecipients
                    ? " "
                    : "This role has no role above it in the hierarchy — it can't be assigned a new manager here."}
                </Typography>
              )}

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, flexWrap: "wrap", gap: 1 }}>
                <Typography fontSize={13} fontWeight={700}>
                  Direct reports — tick a subset, assign, repeat for others
                </Typography>
                {assignedReportIds.length > 0 && (
                  <Typography fontSize={12} color={COLORS.blue} sx={{ cursor: "pointer", fontWeight: 600 }} onClick={clearAllAssignments}>
                    Clear all assignments
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
                <TextField
                  size="small"
                  placeholder="Search by name…"
                  value={reportSearch}
                  onChange={(e) => setReportSearch(e.target.value)}
                  sx={{ ...fieldSx, width: 200 }}
                />
                <FilterChip label="Has a team" active={reportHasTeamOnly} onClick={() => setReportHasTeamOnly((v) => !v)} />
                {distinctReportRoles.length > 1 && (
                  <>
                    <FilterChip label="All roles" active={!reportRoleFilter} onClick={() => setReportRoleFilter(null)} />
                    {distinctReportRoles.map((title) => (
                      <FilterChip key={title} label={title} active={reportRoleFilter === title} onClick={() => setReportRoleFilter(title)} />
                    ))}
                  </>
                )}
              </Box>
              {filteredReports.length === 0 && (
                <Typography fontSize={13} color={COLORS.textSecondary} sx={{ mb: 2 }}>
                  No reports match these filters.
                </Typography>
              )}
              <List dense disablePadding sx={{ mb: 2, border: `1px solid ${COLORS.border}`, borderRadius: 2 }}>
                {filteredReports.map((r) => {
                  const isChecked = checkedReportIds.includes(r.userId);
                  const assignedTo = assignments[r.userId];
                  return (
                    <ListItem
                      key={r.userId}
                      onClick={() => toggleChecked(r.userId)}
                      sx={{
                        cursor: "pointer",
                        borderBottom: `1px solid ${COLORS.border}`,
                        "&:last-child": { borderBottom: "none" },
                        bgcolor: assignedTo ? COLORS.blueLight : isChecked ? COLORS.sunken : "transparent",
                      }}
                    >
                      <CircleCheckbox checked={isChecked} onChange={() => toggleChecked(r.userId)} onClick={(e) => e.stopPropagation()} />
                      <ListItemText
                        primaryTypographyProps={{ fontSize: 13.5, fontWeight: 600 }}
                        secondaryTypographyProps={{ fontSize: 12 }}
                        primary={r.name}
                        secondary={[
                          r.role?.title,
                          r.email,
                          r.lastMovedAt ? `last moved ${new Date(r.lastMovedAt).toLocaleDateString()}` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {!r.isActive && (
                          <Chip label="Inactive" size="small" sx={{ fontSize: 12, fontWeight: 500, bgcolor: COLORS.sunken, color: COLORS.orange, border: `1px solid ${COLORS.border}` }} />
                        )}
                        {pendingApprovalCountByReportId[r.userId] > 0 && (
                          <Chip
                            label={`${pendingApprovalCountByReportId[r.userId]} pending approval${pendingApprovalCountByReportId[r.userId] === 1 ? "" : "s"}`}
                            size="small"
                            sx={{ fontSize: 12, fontWeight: 500, bgcolor: "#fff", border: `1px solid ${COLORS.orange}`, color: COLORS.orange }}
                          />
                        )}
                        {r.directReportsCount > 0 && (
                          <Chip
                            label={`Manages ${r.directReportsCount}`}
                            size="small"
                            sx={{ fontSize: 12, fontWeight: 500, bgcolor: "#fff", border: `1px solid ${COLORS.border}`, color: COLORS.textSecondary }}
                          />
                        )}
                        {assignedTo ? (
                          <>
                            <Chip label={`→ ${assignedTo.name}`} size="small" sx={{ fontSize: 12, fontWeight: 600, bgcolor: COLORS.blue, color: "#fff" }} />
                            <Typography
                              fontSize={11.5}
                              color={COLORS.red}
                              sx={{ cursor: "pointer", fontWeight: 600 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeAssignment(r.userId);
                              }}
                            >
                              Remove
                            </Typography>
                          </>
                        ) : (
                          <Chip label={`Currently reports to ${manager?.name}`} size="small" sx={{ fontSize: 12, bgcolor: COLORS.sunken, color: COLORS.textSecondary }} />
                        )}
                      </Box>
                    </ListItem>
                  );
                })}
              </List>

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <PrimaryButton onClick={goToReview} disabled={assignedReportIds.length === 0}>
                  Review & Confirm
                </PrimaryButton>
              </Box>
            </>
          )}
        </Box>

        {reviewReady && (
          <>
            <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
              <Typography fontSize={13} fontWeight={700} sx={{ mb: 1.5 }}>
                Pending approvals
              </Typography>
              {totalPendingApprovals === 0 ? (
                <Typography fontSize={13.5} color={COLORS.textSecondary} sx={{ mb: 2 }}>
                  Nothing currently pending with {manager?.name} for these reports' leads.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                  {APPROVAL_KINDS.map((kind) => {
                    if (kind.items.length === 0) return null;
                    const allChecked = kind.items.every((r) => kind.selectedIds.includes(r[kind.idField]));
                    const someChecked = kind.items.some((r) => kind.selectedIds.includes(r[kind.idField]));
                    const toggleAll = () => kind.items.forEach((r) => (allChecked === kind.selectedIds.includes(r[kind.idField])) && kind.onToggle(r[kind.idField]));
                    return (
                      <Card
                        key={kind.key}
                        elevation={0}
                        sx={{ border: `1px solid ${someChecked ? COLORS.blue : COLORS.border}`, borderRadius: 3, bgcolor: someChecked ? COLORS.blueLight : "#fff", overflow: "hidden" }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", p: 1.5, cursor: "pointer" }} onClick={toggleAll}>
                          <CircleCheckbox checked={allChecked} indeterminate={someChecked && !allChecked} onChange={toggleAll} onClick={(e) => e.stopPropagation()} />
                          <Box sx={{ flex: 1 }}>
                            <Typography fontSize={13.5} fontWeight={600}>{kind.label}</Typography>
                          </Box>
                          <Chip label={`${kind.items.length} lead${kind.items.length === 1 ? "" : "s"}`} size="small" sx={{ fontSize: 12, fontWeight: 500, bgcolor: COLORS.sunken, color: COLORS.textSecondary }} />
                        </Box>
                        <List dense disablePadding sx={{ px: 1.5, pb: 1 }}>
                          {kind.items.map((r) => {
                            const isChecked = kind.selectedIds.includes(r[kind.idField]);
                            const destination = assignments[r.salesid]?.name || "—";
                            return (
                              <ListItem key={r[kind.idField]} disableGutters sx={{ bgcolor: isChecked ? COLORS.blueLight : "transparent", borderRadius: 1 }}>
                                <CircleCheckbox size="small" checked={isChecked} onChange={() => kind.onToggle(r[kind.idField])} />
                                <ListItemText
                                  primaryTypographyProps={{ fontSize: 13.5 }}
                                  secondaryTypographyProps={{ fontSize: 12 }}
                                  primary={r.leadname || r.leaddisplayid || (r.leadId ? r.leadId.slice(0, 8) : "—")}
                                  secondary={`${r.salesname ? `SM: ${r.salesname} — ` : ""}${r.leaddisplayid || ""}${r.leadstatus ? ` — ${r.leadstatus}` : ""}${r.verificationStatus ? ` — Site visit: ${r.verificationStatus}` : ""} — approval moving from ${manager?.name || "the old manager"} to ${destination}`}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </Card>
                    );
                  })}
                </Box>
              )}
              <Typography fontSize={12} color={COLORS.textSecondary} sx={{ display: "block" }}>
                Tick to carry an approval forward to the report's new manager. Leave unticked to keep it with {manager?.name}.
              </Typography>
            </Box>

            <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
              <Typography fontSize={13} fontWeight={700} sx={{ mb: 1 }}>
                Summary
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
                {summaryLines.map((line, i) => (
                  <Typography key={i} fontSize={13.5}>
                    • {line}
                  </Typography>
                ))}
              </Box>
              {groupList.length > 1 && (
                <Typography fontSize={12} color={COLORS.textSecondary} sx={{ mb: 2 }}>
                  Note: with {groupList.length} different recipients, this submits as {groupList.length} separate transfers (one per recipient) — each gets its own Transfer History entry. If one fails partway through, earlier ones already completed are not undone automatically.
                </Typography>
              )}

              <FieldLabel>
                Reason <span style={{ color: COLORS.red }}>*</span>
              </FieldLabel>
              <TextField
                required
                multiline
                minRows={2}
                fullWidth
                placeholder="e.g. Amit's team split across two managers, effective 27 Aug 2026"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                error={reason.trim().length === 0}
                helperText={reason.trim().length === 0 ? "A reason is required before this transfer can be confirmed." : " "}
                sx={fieldSx}
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start", mt: 2 }}>
                <Box sx={{ textAlign: "right" }}>
                  <PrimaryButton onClick={() => setConfirmDialogOpen(true)} disabled={confirmDisabled}>
                    {confirming ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Confirm rearrange"}
                  </PrimaryButton>
                  {!canExecute && (
                    <Typography fontSize={12} color={COLORS.textSecondary} sx={{ mt: 1, maxWidth: 260 }}>
                      Your role has view-only access — you can browse this transfer, but can't confirm it.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </>
        )}

        <WorkTransferNotice notice={notice} onClose={() => setNotice(null)} />

        <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Confirm this rearrange?</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1 }}>
              {summaryLines.map((line, i) => (
                <Typography key={i} fontSize={13.5}>
                  • {line}
                </Typography>
              ))}
            </Box>
            <Typography fontSize={12.5} color={COLORS.textSecondary}>
              This cannot be undone automatically — a separate manual rearrange would be needed to reverse it.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <SecondaryButton onClick={() => setConfirmDialogOpen(false)}>No, go back</SecondaryButton>
            <PrimaryButton
              onClick={() => {
                setConfirmDialogOpen(false);
                confirmTransfer();
              }}
            >
              Yes, confirm
            </PrimaryButton>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
